<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'requests' }"
          >Requests</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4" v-if="form">
      <div class="d-flex my-6">
        <v-tabs v-if="file">
          <v-tab v-for="(item, key) in tabItems" :key="key">{{
            item.tab
          }}</v-tab>

          <v-tab-item class="row mt-6 pa-0">
            <v-col class="col-md-5 col-sm-12 col-xs-12">
              <h3 class="py-5 primary--text">File Information</h3>

              <label class="text-field-label">Owner Name</label>
              <v-text-field
                flat
                solo
                readonly
                class="mt-2"
                v-model="file.user.full_name"
              ></v-text-field>

              <label class="text-field-label">Owner USI Number</label>
              <v-text-field
                flat
                solo
                readonly
                class="mt-2"
                v-model="file.user.usi"
              ></v-text-field>

              <div
                v-for="(information, key) in file.file_information_types"
                :key="key"
              >
                <label class="text-field-label">{{ information.label }}</label>
                <v-text-field
                  flat
                  solo
                  readonly
                  class="mt-2"
                  :value="
                    information.field_type === 'date'
                      ? formatDate(information.value)
                      : information.value
                  "
                ></v-text-field>
              </div>
            </v-col>
            <v-col class="col-md-5 col-sm-12 col-xs-12">
              <h3 class="py-5 primary--text">Request Information</h3>

              <label class="text-field-label">Last Updated</label>
              <v-text-field
                flat
                solo
                readonly
                class="mt-2"
                :value="formatDate(file.updated_at)"
              ></v-text-field>

              <label class="text-field-label">Status</label>
              <v-text-field
                flat
                solo
                readonly
                class="mt-2"
                :value="file.status.toUpperCase()"
              ></v-text-field>

              <label class="text-field-label">Reason</label>
              <v-textarea
                flat
                solo
                readonly
                class="mt-2"
                v-model="reason"
              ></v-textarea>
              <v-row>
                <v-col
                  cols="6"
                  v-for="(option, i) in options"
                  :key="i"
                  align-self="center"
                >
                  <v-btn
                    block
                    :color="i < 1 ? 'primary' : 'none'"
                    class="text-capitalize"
                    :loading="loading"
                    @click="getReason(option)"
                  >
                    {{ option }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-tab-item>

          <v-tab-item>
            <v-row>
              <v-col
                v-for="file in file.files"
                :key="file.id"
                class="d-flex child-flex clickable"
                cols="3"
              >
                <v-img
                  :src="
                    file.mime_type === 'application/pdf'
                      ? require('@/assets/icons/adobe-acrobat.svg')
                      : file.url
                  "
                  max-height="350"
                  max-width="250"
                  class="grey lighten-2"
                  contain
                  @click="showFile(file)"
                >
                  <template v-slot:placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="grey lighten-5"
                      ></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs>
      </div>
    </div>

    <v-overlay absolute :value="loading" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <ReasonModal
      :status="form.status"
      v-model="showReasonModal"
      @verify="showConfirmation"
    />
    <ConfirmationModal
      v-model="showConfirmationModal"
      @verify="verifyRequest"
    />
    <FileModal v-model="showFileModal" :file="clickedFile" />
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mdiChevronLeft, mdiCheck, mdiAlertCircleOutline } from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import ConfirmationModal from './components/ConfirmationModal'
import ReasonModal from './components/ReasonModal'
import FileModal from './components/FileModal'
import dayjs from 'dayjs'

import Form from '@/utils/form'

export default {
  name: 'RequestDetails',

  components: {
    AppBarNavIcon,
    ConfirmationModal,
    ReasonModal,
    FileModal
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        check: mdiCheck,
        alert: mdiAlertCircleOutline
      },
      loading: false,
      file: null,
      form: new Form({
        status: '',
        reason: ''
      }),
      snackbar: {
        show: false,
        message: null,
        color: ''
      },
      tabItems: [{ tab: 'File Details' }, { tab: 'File Uploads' }],
      reason: '',
      clickedFile: null,
      showConfirmationModal: false,
      showReasonModal: false,
      showFileModal: false
    }
  },

  computed: {
    ...mapState({
      request: state => state.request.requestDetails
    }),
    options() {
      if (this.file.status === 'valid') {
        return ['pending approval', 'reject']
      } else if (this.file.status === 'rejected') {
        return ['valid', 'pending approval']
      }

      return ['valid', 'reject']
    }
  },

  methods: {
    ...mapActions({
      getRequestDetails: 'request/getRequestDetails'
    }),

    ...mapMutations({
      clearRequestDetails: 'request/clearRequestDetails'
    }),

    getReason(option) {
      this.showReasonModal = true
      this.form.status = option
    },

    showConfirmation(reason) {
      this.showConfirmationModal = true
      this.form.reason = reason
    },

    async verifyRequest() {
      if (this.form.status === 'reject') {
        this.form.status = 'rejected'
      }

      await this.$api
        .post(`admin/files/${this.request.id}/verify`, this.form.$data())
        .then(({ data }) => {
          const response = data.data
          if (response.status === 'valid') {
            this.showSnackbar('Accepted request successfully', 'success')
          } else if (response.status === 'pending approval') {
            this.showSnackbar(
              'Changed to pending request successfully',
              'warning'
            )
          } else {
            this.showSnackbar('Rejected request successfully', 'error')
          }
          this.file.status = response.status
          this.reason = response.status_reason
          this.form.$reset()
        })
    },

    showFile(file) {
      this.showFileModal = true
      this.clickedFile = file
    },

    async getRequest() {
      this.loading = true
      await this.getRequestDetails(this.$route.params.id)
      this.file = new Form(this.request)
      this.reason = this.file.status_reason
      this.loading = false
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },

    formatDate(date) {
      return dayjs
        .utc(date)
        .local()
        .format('MMM DD, YYYY hh:mm A')
    }
  },

  created() {
    this.getRequest()
  },

  watch: {
    $route() {
      this.getRequest()
    }
  }
}
</script>
